import { AxiosRequestConfig } from "axios";
import { httpService } from "./HttpServices";

export interface EmailAnalysisResult {
  originalData: {
    domain: string;
    overallScore: number;
    deliverabilityStatus: string;
    summary: {
      spf: RecordSummary;
      dmarc: RecordSummary;
      dkim: RecordSummary;
      mx: RecordSummary;
      blacklist: RecordSummary;
      reverseDNS: RecordSummary;
      smtpBanner: RecordSummary;
      openRelay: RecordSummary;
      ssl: RecordSummary;
      mtasts: RecordSummary;
      tlsrpt: RecordSummary;
      bimi: RecordSummary;
      domainExpiration: RecordSummary;
      safeBrowsing: RecordSummary;
    };
    issuesSummary: {
      critical: string[];
      warning: string[];
      recommendations: string[];
    };
    timestamp: string;
  };
  aiAnalysis: string;
  timestamp: string;
}

export interface EmailMonitoring {
  id: string;
  userEmail: string;
  parameterEmail: string;
  frequency: MonitoringFrequency;
  preferredTime?: string;
  preferredDay?: WeekDay;
  preferredDate?: number;
  isActive: boolean;
  lastChecked?: string;
  nextCheckDue?: string;
  createdAt: string;
  updatedAt: string;
  analysisHistory: Array<{
    timestamp: string;
    score: number;
    status: string;
    issues: any;
  }>;
}

export enum MonitoringFrequency {
  Daily = "daily",
  Weekly = "weekly",
  Monthly = "monthly",
}

export enum WeekDay {
  Sunday = 0,
  Monday = 1,
  Tuesday = 2,
  Wednesday = 3,
  Thursday = 4,
  Friday = 5,
  Saturday = 6,
}

export interface CreateMonitoringRequest {
  userEmail: string;
  parameterEmail: string;
  frequency: MonitoringFrequency;
  preferredTime?: string;
  preferredDay?: WeekDay;
  preferredDate?: number;
}

export interface UpdateMonitoringRequest {
  frequency?: MonitoringFrequency;
  parameterEmail?: string;
  isActive?: boolean;
}

interface RecordSummary {
  status: "success" | "warning" | "error" | "info";
  message: string;
  details: any;
  score: number;
}

export interface ApiResponse<T> {
  data: T;
  message: string;
  status: number;
}

interface MonitoringResponse {
  _id: string;
  userEmail: string;
  parameterEmail: string;
  frequency: MonitoringFrequency;
  preferredTime?: string;
  preferredDay?: WeekDay;
  preferredDate?: number;
  isActive: boolean;
  lastChecked?: string;
  nextCheckDue?: string;
  createdAt: string;
  updatedAt: string;
  analysisHistory: Array<{
    timestamp: string;
    score: number;
    status: string;
    issues: any;
  }>;
}

// Transform function to convert _id to id
const transformMonitoring = (
  monitoring: MonitoringResponse
): EmailMonitoring => ({
  ...monitoring,
  id: monitoring._id,
});

export class EmailHygieneService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = "/tools";
  }

  async analyzeEmail(
    domain: string,
    config?: AxiosRequestConfig
  ): Promise<{ data: EmailAnalysisResult; message: string }> {
    try {
      const response = await httpService.post<ApiResponse<EmailAnalysisResult>>(
        `${this.baseUrl}/analyze-email`,
        { domain },
        config
      );

      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to analyze email domain.";
      throw new Error(errorMessage);
    }
  }

  async createMonitoring(
    request: CreateMonitoringRequest
  ): Promise<{ data: EmailMonitoring; message: string }> {
    try {
      const response = await httpService.post<ApiResponse<EmailMonitoring>>(
        `${this.baseUrl}/monitor-email`,
        request
      );

      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to create monitoring.";
      throw new Error(errorMessage);
    }
  }

  async getMonitoringList(params?: {
    status?: "active" | "inactive";
    frequency?: MonitoringFrequency;
  }): Promise<{
    data: { total: number; items: EmailMonitoring[] };
    message: string;
  }> {
    try {
      const response = await httpService.get<
        ApiResponse<{ total: number; items: MonitoringResponse[] }>
      >(`${this.baseUrl}/monitoring-list`, { params });

      return {
        data: {
          total: response.data.total,
          items: response.data.items.map(transformMonitoring),
        },
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to fetch monitoring list.";
      throw new Error(errorMessage);
    }
  }

  async updateMonitoring(
    monitoringId: string,
    request: UpdateMonitoringRequest
  ): Promise<{ data: EmailMonitoring; message: string }> {
    try {
      const response = await httpService.put<ApiResponse<EmailMonitoring>>(
        `${this.baseUrl}/monitor-email/${monitoringId}`,
        request
      );

      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to update monitoring.";
      throw new Error(errorMessage);
    }
  }

  async deleteMonitoring(
    monitoringId: string
  ): Promise<{ data: { id: string }; message: string }> {
    if (!monitoringId) {
      throw new Error("Monitoring ID is required");
    }

    try {
      const response = await httpService.delete<ApiResponse<{ id: string }>>(
        `${this.baseUrl}/monitor-email/${monitoringId}`
      );

      return {
        data: { id: monitoringId },
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to delete monitoring.";
      throw new Error(errorMessage);
    }
  }
}

export const emailHygieneService = new EmailHygieneService();
